import { useEffect, useState } from "react";
import { CalendarRange, Clock, MapPin, Pencil, Plus, Trash2, Upload, Users } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProgrammeFormDialog } from "@/components/ProgrammeFormDialog";
import { ProgrammeSheet } from "@/components/ProgrammeSheet";
import { api, apiError, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Every training a unit ran itself, with its hours — Coursera stays on its own panel. */
export const TrainingProgrammes = () => {
  const { shows } = useAuth();
  const [data, setData] = useState(null);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, programme: null, past: false });
  const [openId, setOpenId] = useState(null);

  const load = () => {
    api
      .get("/training/programmes", {
        params: { kind: kind === "all" ? "" : kind, q },
      })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, q]);

  const remove = async (programme) => {
    if (
      !window.confirm(
        `Are you sure? "${programme.title}" and its ${programme.participants} participant(s) ` +
          "will be taken off the training register.",
      )
    )
      return;
    try {
      await api.delete(`/training/programmes/${programme.id}`);
      toast.success(`${programme.title} removed.`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const totals = data?.totals;

  return (
    <div className="mt-8 border border-border bg-card" data-testid="training-programmes">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-border p-5">
        <div className="max-w-2xl">
          <div className="label-caps">Classroom, institute, webinar, workshop, site visit</div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            Training the units ran themselves
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            One record per programme, one row per participant, and a mark for every day of it —{" "}
            <b>a full day is 8 hours and half a day 4</b>, so a unit's training hours add
            themselves up. The participant list goes out and comes back in Excel.
          </p>
        </div>
        {data?.can_add && shows("training.programme_add") && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              data-testid="add-programme-button"
              onClick={() => setForm({ open: true, programme: null, past: false })}
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              <Plus className="mr-2 h-4 w-4" /> Add a training
            </Button>
            <Button
              variant="outline"
              data-testid="load-past-training-button"
              onClick={() => setForm({ open: true, programme: null, past: true })}
              className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest"
            >
              <Upload className="mr-2 h-4 w-4" /> Load an old training
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-px bg-border lg:grid-cols-4">
        {[
          ["Programmes", totals?.programmes ?? 0, "programme-stat-total"],
          ["Participants", totals?.participants ?? 0, "programme-stat-people"],
          ["Training hours", totals?.hours ?? 0, "programme-stat-hours"],
          ["Man-days", totals?.man_days ?? 0, "programme-stat-man-days"],
        ].map(([label, value, tid]) => (
          <div key={label} className="bg-card p-4" data-testid={tid}>
            <div className="label-caps">{label}</div>
            <div className="font-data mt-2 text-2xl font-bold">{value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 border-t border-border p-5">
        <Input
          placeholder="Search a programme, a venue or a trainer…"
          className="h-11 max-w-sm rounded-none"
          data-testid="programme-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="h-11 w-[240px] rounded-none" data-testid="programme-kind-filter">
            <SelectValue placeholder="Type of training" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Every type</SelectItem>
            {(data?.types || []).map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-px bg-border lg:grid-cols-2 xl:grid-cols-3">
        {(data?.programmes || []).map((p) => (
          <div key={p.id} className="bg-card p-4" data-testid={`programme-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="label-caps text-primary">{p.kind}</span>
                <button
                  type="button"
                  data-testid={`programme-open-${p.id}`}
                  onClick={() => setOpenId(p.id)}
                  className="block text-left text-sm font-bold transition-colors duration-200 hover:text-primary"
                >
                  {p.title}
                </button>
                <div className="font-data mt-1 space-y-0.5 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <CalendarRange className="h-3 w-3" /> {fmtDay(p.from_date)} →{" "}
                    {fmtDay(p.to_date)} · {p.day_count} day(s)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-3 w-3" /> {p.plant} · {p.mode}
                    {p.venue ? ` · ${p.venue}` : ""}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Users className="h-3 w-3" /> {p.participants} participant(s)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3 w-3" /> {p.hours} hours · {p.man_days} man-days
                  </span>
                </div>
              </div>
              {p.can_edit && (
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    title="Correct it"
                    data-testid={`programme-edit-${p.id}`}
                    onClick={() => setForm({ open: true, programme: p, past: false })}
                    className="border border-border p-1 text-muted-foreground transition-colors hover:border-primary hover:text-primary"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    title="Remove it"
                    data-testid={`programme-remove-${p.id}`}
                    onClick={() => remove(p)}
                    className="border border-border p-1 text-muted-foreground transition-colors hover:border-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
              <span className="border border-[hsl(var(--healthy))] px-1.5 py-0.5 text-[hsl(var(--healthy))]">
                {p.days_full} full
              </span>
              <span className="border border-[hsl(var(--watch))] px-1.5 py-0.5 text-[hsl(var(--watch))]">
                {p.days_half} half
              </span>
              <span className="border border-[hsl(var(--critical))] px-1.5 py-0.5 text-[hsl(var(--critical))]">
                {p.days_absent} absent
              </span>
              <span className="border border-border px-1.5 py-0.5 text-muted-foreground">
                {p.attendance_pct}% of the hours on offer
              </span>
            </div>
          </div>
        ))}
        {data && !data.programmes.length && (
          <p className="bg-card p-6 text-sm text-muted-foreground" data-testid="programmes-empty">
            No training recorded here yet. {data.can_add ? 'Press "Add a training" to put the first one on.' : ""}
          </p>
        )}
      </div>

      <ProgrammeFormDialog
        open={form.open}
        onOpenChange={(o) => setForm((f) => ({ ...f, open: o }))}
        programme={form.programme}
        past={form.past}
        types={data?.types || []}
        modes={data?.modes || []}
        plants={data?.plants || []}
        myPlant={data?.my_plant || ""}
        onSaved={load}
      />
      <ProgrammeSheet programmeId={openId} onClose={() => setOpenId(null)} onChanged={load} />
    </div>
  );
};
